// src/components/album/AlbumMediaCard.jsx
import React, { useState, useEffect, useRef } from 'react';
import { 
  Heart, 
  Download, 
  Share2, 
  Play, 
  Eye, 
  MessageCircle,
  Edit3,
  Save,
  Pause,
  ChevronRight,
  ChevronLeft,
  Activity,
  ArrowLeft,
  RotateCcw,
  FileText,
  HelpCircle,
  Info,
  X,
  Volume2,
  VolumeX,
  Star,
  DollarSign,
  ExternalLink,
  Calendar,
  Tag,
  Building2,
  Package,
  Users,
  Globe,
  AlertCircle,
  Check,
  Sparkles,
  Mic,
  Video,
  Camera,
  Type,
  Link,
  Mail,
  ImageIcon,
  ShoppingCart,
  MessageSquare,
  Loader2,
  ChevronDown,
  MoreHorizontal, 
  ChevronUp
} from 'lucide-react';
import { albumService } from '../../services/albumService';
import { MediaExpressionsModal } from './MediaExpressionsModal';
import TransactionModal from '../album/TransactionModal';
import albumAnalyticsService from '../../services/albumAnalyticsService';
import { firestoreService } from '../../services/firestoreService';
import { FileUpload } from '../common/FileUpload';
import { flipCardService } from '../../services/flipCardService';
import { FlipCardContent } from './FlipCardContent';

import { MediaVoiceIndicator } from './voice/MediaVoiceIndicator';
import { MediaVoiceNotes } from './voice/MediaVoiceNotes';
import { VoiceRecorder } from './voice/VoiceRecorder';
import { statusService } from '../../services/statusService';

// AlbumMediaCard.jsx - Add metadata normalization helper at top of component (after imports)

const normalizeMediaMetadata = (mediaItem) => {
  // If metadata is nested, flatten it to root
  if (mediaItem.metadata && typeof mediaItem.metadata === 'object') {
    return {
      ...mediaItem,
      ...mediaItem.metadata
    };
  }
  return mediaItem;
};

// Field type to icon mapping
const FIELD_ICONS = {
  title: Type,
  description: MessageCircle,
  tags: Tag,
  price: DollarSign,
  brand: Building2,
  brandLogo: ImageIcon,
  quantity: Package,
  sku: Tag,
  rating: Star,
  link: Link,
  location: Globe,
  contact: Users,
  category: Tag,
  allergens: AlertCircle,
  spiceLevel: Star,
  calories: Heart,
  features: Check,
  availability: Eye,
  size: Building2,
  client: Users,
  projectDate: Calendar,
  skills: Tag,
  awards: Star,
  uploadDate: Calendar,
  photographer: Camera
};

// Dynamic Field Input Component
// Dynamic Field Input Component - ADD AFTER LINE 101
const DynamicFieldInput = ({ 
  fieldKey, 
  fieldConfig, 
  value, 
  onChange, 
  isEditing,
  mediaItem, 
  albumSettings,
  customStyling,
  bookId,
  galleryId,
  mediaItemId
}) => {
  const [localValue, setLocalValue] = useState(value || '');
  const [starRating, setStarRating] = useState(parseInt(value) || 0);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const textColor = customStyling?.textColor || '#1f2937';
  const labelStyle = { color: textColor };

  React.useEffect(() => {
    setLocalValue(value || '');
    if (fieldConfig.type === 'rating') {
      setStarRating(parseInt(value) || 0);
    }
  }, [value, fieldConfig.type]);

  const handleChange = (newValue) => {
    setLocalValue(newValue);
    onChange(fieldKey, newValue);
  };

  const handleRatingChange = (rating) => {
    setStarRating(rating);
    onChange(fieldKey, rating.toString());
  };

  const handleLogoUpload = async (file) => {
    if (!file || !bookId || !galleryId || !mediaItemId) return;
    
    setUploadingLogo(true);
    try {
      const storagePath = `albums/${bookId}/${galleryId}/${mediaItemId}/brand-logo-${Date.now()}`;
      const result = await firestoreService.uploadFile(file, storagePath);
      
      handleChange(result.url);
    } catch (error) {
      console.error('Error uploading brand logo:', error);
      alert('Failed to upload logo. Please try again.');
    } finally {
      setUploadingLogo(false);
    }
  };

  if (!isEditing) {
    // Display mode
    if (!value) return null;

    // Brand Logo Display
    if (fieldKey === 'brandLogo') {
      return (
        <div className="flex items-center gap-2">
          <img 
            src={value} 
            alt="Brand Logo" 
            className="h-8 w-8 object-contain rounded"
          />
        </div>
      );
    }

    switch (fieldConfig.type) {
      case 'currency':
        return (
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <span className="font-semibold text-green-600">R{value}</span>
          </div>
        );

      case 'rating':
        return (
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className={`h-4 w-4 ${
                  star <= parseInt(value) 
                    ? 'text-yellow-400 fill-current' 
                    : 'text-gray-300'
                }`}
              />
            ))}
            <span className="text-sm text-gray-600 ml-1">({value}/5)</span>
          </div>
        );

      case 'tags':
        const tags = value.split(',').map(tag => tag.trim()).filter(Boolean);
        return (
          <div className="flex flex-wrap gap-1">
            {tags.map((tag, index) => (
              <span 
                key={index}
                className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        );

      case 'status':
        const statusColors = {
          available: 'bg-green-100 text-green-800',
          unavailable: 'bg-red-100 text-red-800',
          limited: 'bg-yellow-100 text-yellow-800'
        };
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            statusColors[value.toLowerCase()] || 'bg-gray-100 text-gray-800'
          }`}>
            {value}
          </span>
        );

      case 'date':
        return (
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <Calendar className="h-4 w-4 text-gray-500" />
            <span>{new Date(value).toLocaleDateString()}</span>
          </div>
        );

      case 'textarea':
        return (
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{value}</p>
        );

      default:
        return (
          <span className="text-sm text-gray-700">{value}</span>
        );
    }
  }

  // Edit mode inputs
  const IconComponent = FIELD_ICONS[fieldKey] || Type;

  // Brand Logo Upload in Edit Mode
  if (fieldKey === 'brandLogo') {
    return (
      <div className="space-y-1">
        <label className="flex items-center gap-2 text-xs font-medium text-gray-700" style={labelStyle}>
          <ImageIcon className="h-3 w-3" />
          {fieldConfig.label}
        </label>
        
        <FileUpload
          onFileSelect={(file, error) => {
            if (error) {
              alert(error);
              return;
            }
            handleLogoUpload(file);
          }}
          accept="image/*"
          maxSize={2}
          loading={uploadingLogo}
          className="h-24"
        >
          {localValue ? (
            <div className="flex flex-col items-center justify-center h-full">
              <img 
                src={localValue} 
                alt="Brand Logo" 
                className="h-16 w-16 object-contain rounded mb-2"
              />
              <span className="text-xs text-gray-500">Click to change</span>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full">
              <ImageIcon className="h-8 w-8 text-gray-400 mb-2" />
              <span className="text-xs text-gray-500">Upload logo</span>
            </div>
          )}
        </FileUpload>
        
        {uploadingLogo && (
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <Loader2 className="h-3 w-3 animate-spin" />
            <span>Uploading...</span>
          </div>
        )}
      </div>
    );
  }

  switch (fieldConfig.type) {
    case 'textarea':
      return (
        <div className="space-y-1">
          <label className="flex items-center gap-2 text-xs font-medium " style={labelStyle} >
            <IconComponent className="h-3 w-3" />
            {fieldConfig.label}
          </label>
          <textarea
            value={localValue}
            onChange={(e) => handleChange(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            placeholder={`Enter ${fieldConfig.label.toLowerCase()}...`}
          />
        </div>
      );

    case 'currency':
      return (
        <div className="space-y-1">
          <label className="flex items-center gap-2 text-xs font-medium text-gray-700" style={labelStyle}>
            <span className="h-5 w-5"> R </span>
            {fieldConfig.label}
          </label>
          <div className="relative">
            <span className="absolute left-3 top-2 text-gray-500">R</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={localValue}
              onChange={(e) => handleChange(e.target.value)}
              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-500 text-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="0.00"
            />
          </div>
        </div>
      );

    case 'rating':
      return (
        <div className="space-y-1">
          <label className="flex items-center gap-2 text-xs font-medium text-gray-700" style={labelStyle}>
            <Star className="h-3 w-3" />
            {fieldConfig.label}
          </label>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => handleRatingChange(star)}
                className="p-1 hover:scale-110 transition-transform"
              >
                <Star
                  className={`h-5 w-5 ${
                    star <= starRating 
                      ? 'text-yellow-400 fill-current' 
                      : 'text-gray-300 hover:text-yellow-200'
                  }`}
                />
              </button>
            ))}
            <span className="text-sm text-gray-600 ml-2">({starRating}/5)</span>
          </div>
        </div>
      );

    case 'tags':
      return (
        <div className="space-y-1">
          <label className="flex items-center gap-2 text-xs font-medium text-gray-700" style={labelStyle}>
            <Tag className="h-3 w-3" />
            {fieldConfig.label}
          </label>
          <input
            type="text"
            value={localValue}
            onChange={(e) => handleChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter tags separated by commas..."
          />
          <p className="text-xs text-gray-500">Separate multiple tags with commas</p>
        </div>
      );

    default:
      return (
        <div className="space-y-1">
          <label className="flex items-center gap-2 text-xs font-medium text-gray-700" style={labelStyle}>
            <IconComponent className="h-3 w-3" />
            {fieldConfig.label}
          </label>
          <input
            type="text"
            value={localValue}
            onChange={(e) => handleChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={`Enter ${fieldConfig.label.toLowerCase()}...`}
          />
        </div>
      );
  }
};

export const MediaCard = ({ 
  user,
  mediaItem, 
  index, 
  onClick, 
  bookId,
  isOwner,
  primaryColor = '#190192',
  albumSettings = null,
  customStyling,
  bookData = null,
  galleryId,
  onOpenModal,
  uniformGrid = false,
  bento = false,
  hero = false,
  supporting = false,
  constellation = false,
  riverFlow = false,
  timeline = false,
  gallery,
  // NEW: Layout settings (all optional)
  cropToFit = null, // null means use default behavior
  featured = false,
  size = 'medium',
  aspectRatio = null, // null means use default behavior
  showOverlay = null, // null means use default behavior
  showInteractions = null, // null means use default behavior
  rotation = 0,
  
  // NEW: Custom styling overrides (optional)
  customClasses = '',
  style = {},
  onUpdate,
  albumType,
  features = {},
  onReaction,
  onTagUpdate,
  onContentCreate,
  onRecordVoice

}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLiked, setIsLiked] = useState(mediaItem.interactions?.isLiked || false);
  const [isMuted, setIsMuted] = useState(true);
  const [showAllFields, setShowAllFields] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);
  const videoRef = useRef(null);

   // Touch/swipe handling - only for mobile devices
  const [showAllInteractions, setShowAllInteractions] = useState(false);
  const [showInteractionButtons, setShowInteractionButtons] = useState(false);
  const [touchStart, setTouchStart] = useState({ x: 0, y: 0 });
  const [touchEnd, setTouchEnd] = useState({ x: 0, y: 0 });
  const [isSwipeInProgress, setIsSwipeInProgress] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);

  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [transactionType, setTransactionType] = useState(null);
  const [transactionConfig, setTransactionConfig] = useState(null);

  const [showContentArea, setShowContentArea] = useState(false);
  const [showExpressionsModal, setShowExpressionsModal] = useState(false);
  const [fieldValues, setFieldValues] = useState({});

  const [showAdvancedReactions, setShowAdvancedReactions] = useState(false);
  const [isRecordingAudio, setIsRecordingAudio] = useState(false);
  const [isRecordingVideo, setIsRecordingVideo] = useState(false);
  const [showAITags, setShowAITags] = useState(false);
  const [playingAudio, setPlayingAudio] = useState(null);
  const [uploadingReaction, setUploadingReaction] = useState(false);
  const [showFileUpload, setShowFileUpload] = useState(false);

  const [isFlipped, setIsFlipped] = useState(false);
  const [backContentData, setBackContentData] = useState(null);
  const [loadingBackContent, setLoadingBackContent] = useState(false);

  const [showVoicePanel, setShowVoicePanel] = useState(false);

  const [showTeamStatusButtons, setShowTeamStatusButtons] = useState(false);
  const [selectedTeamStatus, setSelectedTeamStatus] = useState(null);

  const [showTransactionConfirm, setShowTransactionConfirm] = useState(false);
  const [pendingTransaction, setPendingTransaction] = useState(null);
  const [transactionFormData, setTransactionFormData] = useState({
    startDate: '',
    endDate: '',
    quantity: 1,
    area: ''
  });

  const audioRecorderRef = useRef(null);
  const videoRecorderRef = useRef(null);
  const photoInputRef = useRef(null);
  
  const cardRef = useRef(null);
  const touchTimeoutRef = useRef(null);
  const flipTimeoutRef = useRef(null);

  const voiceSettings = bookData?.voiceSettings;
  const isMediaVoiceEnabled = voiceSettings?.mediaVoiceNotes?.enabled === true;
  
  const enableFlip = bookData?.cardSettings?.enableFlipCards || bookData?.settings?.enableFlipCards;
  const hasMediaContent = !!(mediaItem.url);
  const supportsFrontBack = hasMediaContent && !isEditing;

  const isTeamCategory = bookData?.albumSettings?.albumCategory === 'teams';
  const teamSubcategory = bookData?.albumSettings?.albumSubcategory;
  const quickStatusConfig = bookData?.albumSettings?.quickStatusOptions || {};

  const normalizedMedia = normalizeMediaMetadata(mediaItem);

  // Initialize field values from mediaItem

  useEffect(() => {
    if (albumSettings?.enabledFields && mediaItem) {
      const normalizedMedia = normalizeMediaMetadata(mediaItem);
      const initialValues = {};
      
      Object.keys(albumSettings.enabledFields).forEach(fieldKey => {
        if (albumSettings.enabledFields[fieldKey]) {
          const value = normalizedMedia[fieldKey];
          if (value !== undefined && value !== null && value !== '') {
            initialValues[fieldKey] = value;
          }
        }
      });
      
      setFieldValues(initialValues);
    }
  }, [mediaItem?.id, albumSettings?.enabledFields]);

  useEffect(() => {
    const checkMobileDevice = () => {
      const isMobile = window.innerWidth <= 768 && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
      setIsMobileDevice(isMobile);
    };
    
    checkMobileDevice();
    window.addEventListener('resize', checkMobileDevice);
    return () => window.removeEventListener('resize', checkMobileDevice);
  }, []);

  // Auto-hide interaction buttons after 3 seconds on mobile devices
  useEffect(() => {
    if (isMobileDevice && showInteractionButtons) {
      if (touchTimeoutRef.current) {
        clearTimeout(touchTimeoutRef.current);
      }
      
      touchTimeoutRef.current = setTimeout(() => {
        setShowInteractionButtons(false);
      }, 3000);
    }

    return () => {
      if (touchTimeoutRef.current) {
        clearTimeout(touchTimeoutRef.current);
      }
    };
  }, [showInteractionButtons, isMobileDevice]);

  useEffect(() => {
    setShowAllInteractions(false);
  }, [mediaItem.id]);

  useEffect(() => {
    if (enableFlip && mediaItem?.backContentId && galleryId) {
      loadBackContent();
    }
  }, [mediaItem?.backContentId, enableFlip, galleryId]);

  const loadBackContent = async () => {
    if (!flipCardService?.getCardContent) {
      console.warn('flipCardService.getCardContent not available');
      return;
    }
    
    try {
      setLoadingBackContent(true);
      const content = await flipCardService.getCardContent(
        bookId, 
        galleryId, 
        mediaItem.backContentId
      );
      setBackContentData(content);
    } catch (error) {
      console.error('Error loading back content:', error);
    } finally {
      setLoadingBackContent(false);
    }
  };

  const shouldShowFlipButton = () => {
    return enableFlip && (backContentData || mediaItem?.backContentId || isOwner);
  };

  const getFlipButtonIcon = () => {
    if (isFlipped) {
      return ArrowLeft;
    }
    
    if (!backContentData) return RotateCcw;
    
    const contentTypeIcons = {
      'form': MessageCircle,
      'article': FileText,
      'quiz': HelpCircle,
      'survey': MessageCircle,
      'product': ShoppingCart,
      'event': Calendar,
      'profile': Users,
      'review': Star
    };
    
    return contentTypeIcons[backContentData.type] || RotateCcw;
  };

  const getCardClasses = () => {
    let classes = 'group relative overflow-hidden transition-all duration-300 cursor-pointer bg-white rounded-md shadow-sm hover:shadow-lg';
    
    // Layout-specific classes
    if (uniformGrid) {
      classes += ' w-full h-full';
    }
    
    if (bento) {
      classes += featured ? ' hover:scale-[1.02]' : ' hover:scale-105';
    }
    
    if (hero) {
      classes += ' w-full h-full min-h-[40vh] hover:scale-[1.01]';
    }
    
    if (supporting) {
      classes += ' hover:scale-105';
    }
    
    if (constellation) {
      classes += ' hover:scale-110 hover:z-10 rounded-full';
    }
    
    if (riverFlow) {
      classes += ' hover:scale-105 rounded-xl';
    }
    
    if (timeline) {
      classes += ' border-l-4 ml-4';
    }
    
    // Size-based classes for special layouts
    if (constellation || riverFlow) {
      const sizeClasses = {
        small: 'max-w-32',
        medium: 'max-w-48', 
        large: 'max-w-64',
        xl: 'max-w-80'
      };
      classes += ` ${sizeClasses[size] || sizeClasses.medium}`;
    }
    
    return `${classes} ${customClasses}`;
  };

  // NEW: Generate aspect ratio classes
  const getAspectClasses = () => {
    // If aspectRatio is explicitly set, use it
    if (aspectRatio) {
      switch (aspectRatio) {
        case '1:1': return 'aspect-square';
        case '4:3': return 'aspect-[4/3]';
        case '16:9': return 'aspect-video';
        case '3:4': return 'aspect-[3/4]';
        default: return '';
      }
    }
    
    // Layout-specific defaults
    if (constellation || riverFlow) return 'aspect-square';
    if (uniformGrid) return 'aspect-square';
    
    // Default behavior (no aspect ratio constraint)
    return '';
  };

  // NEW: Generate image classes
  const getImageClasses = () => {
    let classes = 'transition-all duration-500 group-hover:scale-105';
    
    // Determine crop behavior
    const shouldCrop = cropToFit !== null ? cropToFit : (uniformGrid || constellation || hero);
    
    if (shouldCrop) {
      classes += ' w-full h-full object-cover';
    } else {
      classes += ' w-full h-auto object-contain';
    }
    
    return classes;
  };

  // NEW: Generate container style
  const getContainerStyle = () => {
    let containerStyle = { ...style };
    
    if (riverFlow && rotation) {
      containerStyle.transform = `rotate(${rotation}deg)`;
    }
    
    if (constellation) {
      containerStyle.borderRadius = '50%';
    }
    
    if (timeline) {
      containerStyle.borderColor = primaryColor;
    }
    
    return containerStyle;
  };

  // Touch handlers for swipe interactions (mobile only)
  const handleTouchStart = (e) => {
    if (!isMobileDevice) return;
    
    const touch = e.touches[0];
    setTouchStart({ x: touch.clientX, y: touch.clientY });
    setTouchEnd({ x: touch.clientX, y: touch.clientY }); // Initialize touchEnd
    setIsSwipeInProgress(true);
    
    // Clear any existing timeouts
    if (touchTimeoutRef.current) {
      clearTimeout(touchTimeoutRef.current);
    }
  };

  const handleTouchMove = (e) => {
    if (!isMobileDevice || !isSwipeInProgress) return;
    
    const touch = e.touches[0];
    setTouchEnd({ x: touch.clientX, y: touch.clientY });
    
    // Prevent default scrolling if it's a horizontal swipe
    const deltaX = Math.abs(touchStart.x - touch.clientX);
    const deltaY = Math.abs(touchStart.y - touch.clientY);
    
    if (deltaX > deltaY && deltaX > 20) {
      e.preventDefault(); // Prevent vertical scrolling for horizontal swipes
    }
  };

  const handleTouchEnd = (e) => {
  // Early exit conditions
  if (!isMobileDevice || !touchStart.x || !touchEnd.x || !isSwipeInProgress) {
    setIsSwipeInProgress(false);
    return;
  }
  
  // ✅ Skip if editing or in content area
  if (isEditing || e.target.closest('.content-area-wrapper')) {
    setIsSwipeInProgress(false);
    return;
  }
  
  const deltaX = touchStart.x - touchEnd.x;
  const deltaY = Math.abs(touchStart.y - touchEnd.y);
  const minSwipeDistance = 60;
  const maxVerticalMovement = 80;
  
  // ✅ Calculate if this was actually a swipe
  const isLeftSwipe = deltaX > minSwipeDistance && deltaY < maxVerticalMovement;
  const isRightSwipe = deltaX < -minSwipeDistance && deltaY < maxVerticalMovement;
  const isSwipe = isLeftSwipe || isRightSwipe;
  
  
  // ✅ Only handle if it's a clear swipe
  if (isSwipe) {
    // Prevent the click event from firing after a swipe
    e.preventDefault();
    e.stopPropagation();
    
    if (isLeftSwipe) {
      console.log('Left swipe detected - showing interactions');
      setShowInteractionButtons(true);
      
      touchTimeoutRef.current = setTimeout(() => {
        setShowInteractionButtons(false);
        setShowAllInteractions(false);
      }, 4000);
    } else if (isRightSwipe) {
      console.log('Right swipe detected - hiding interactions');
      setShowInteractionButtons(false);
      setShowAllInteractions(false);
    }
  }
  // ✅ If not a swipe (just a tap), do nothing - let click event fire
  
  setIsSwipeInProgress(false);
  
  // ✅ Reset touch coordinates
  setTouchStart({ x: 0, y: 0 });
  setTouchEnd({ x: 0, y: 0 });
};

  // RESTORED ORIGINAL INTERACTION HANDLERS
  const handleMouseEnter = () => {
    setIsHovered(true);
    if (mediaItem.type === 'video' && videoRef.current) {
      videoRef.current.play().catch(() => {
        // Ignore autoplay errors
      });
    }
  };

  const canUserRecord = () => {
    const permissions = voiceSettings?.mediaVoiceNotes?.permissions?.canRecord || 'owner-only';
    
    switch (permissions) {
      case 'owner-only':
        return isOwner;
      case 'collaborators':
        return isOwner; // You can add collaborator check here
      case 'authenticated':
        return true; // Assuming user is authenticated if they can see this
      case 'anyone':
        return true;
      default:
        return isOwner;
    }
  };

  const toggleAudioRecording = async () => {
    try {
      if (!isRecordingAudio) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        audioRecorderRef.current = new MediaRecorder(stream);
        
        const chunks = [];
        audioRecorderRef.current.ondataavailable = (e) => chunks.push(e.data);
        audioRecorderRef.current.onstop = () => handleAudioComplete(chunks);
        
        audioRecorderRef.current.start();
        setIsRecordingAudio(true);
      } else {
        audioRecorderRef.current.stop();
        audioRecorderRef.current.stream.getTracks().forEach(track => track.stop());
        setIsRecordingAudio(false);
      }
    } catch (error) {
      console.error('Error with audio recording:', error);
      alert('Could not access microphone. Please check permissions.');
    }
  };

  const handleAudioComplete = async (chunks) => {
    const blob = new Blob(chunks, { type: 'audio/webm' });
    
    // Upload to Firebase Storage
    try {
      const audioUrl = await uploadAudioReaction(blob, mediaItem.id);
      
      // Save reaction to Firestore
      await onReaction(mediaItem.id, 'audio', {
        audioUrl,
        duration: blob.size, // Approximate - should calculate actual duration
        timestamp: new Date()
      });
      
    } catch (error) {
      console.error('Error saving audio reaction:', error);
      alert('Failed to save audio reaction');
    }
  };

  // Video Recording Functions
  const toggleVideoRecording = async () => {
    try {
      if (!isRecordingVideo) {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: { width: 640, height: 480 }, 
          audio: true 
        });
        
        videoRecorderRef.current = new MediaRecorder(stream);
        
        const chunks = [];
        videoRecorderRef.current.ondataavailable = (e) => chunks.push(e.data);
        videoRecorderRef.current.onstop = () => handleVideoComplete(chunks);
        
        videoRecorderRef.current.start();
        setIsRecordingVideo(true);
        
        // Auto-stop after 30 seconds for mobile performance
        setTimeout(() => {
          if (isRecordingVideo) {
            toggleVideoRecording();
          }
        }, 30000);
        
      } else {
        videoRecorderRef.current.stop();
        videoRecorderRef.current.stream.getTracks().forEach(track => track.stop());
        setIsRecordingVideo(false);
      }
    } catch (error) {
      console.error('Error with video recording:', error);
      alert('Could not access camera. Please check permissions.');
    }
  };

  const handleVideoComplete = async (chunks, stream) => {
    const blob = new Blob(chunks, { type: 'video/webm' });
    
    // Clean up stream
    stream.getTracks().forEach(track => track.stop());
    
    if (blob.size > 50 * 1024 * 1024) { // 50MB limit
      alert('Video too large. Please record a shorter video.');
      return;
    }
    
    try {
      setUploadingReaction(true);
      
      const videoFile = new File([blob], `video-reaction-${Date.now()}.webm`, {
        type: 'video/webm'
      });
      
      const uploadResult = await albumService.uploadFile(
        bookId,
        videoFile,
        `reactions/video/${mediaItem.id}`
      );
      
      await onReaction(mediaItem.id, 'video', {
        videoUrl: uploadResult.url,
        fileName: uploadResult.fileName,
        size: blob.size,
        timestamp: new Date()
      });
      
      alert('Video reaction saved successfully!');
      
    } catch (error) {
      console.error('Error saving video reaction:', error);
      alert('Failed to save video reaction');
    } finally {
      setUploadingReaction(false);
    }
  };

  // Photo Response Functions
  const capturePhotoResponse = () => {
    // Check if device has camera capabilities
    if ('MediaDevices' in window && 'getUserMedia' in navigator.mediaDevices) {
      // Offer both options: camera or file picker
      const choice = window.confirm('Take a new photo with camera? (Cancel to select from gallery)');
      if (choice) {
        handleCameraCapture();
      } else {
        setShowFileUpload(true);
      }
    } else {
      // Fallback to file picker only
      setShowFileUpload(true);
    }
  };

  const handleCameraCapture = async () => {
    try {
      setUploadingReaction(true);
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user' } // Front camera for selfie responses
      });
      
      // Create video element to capture frame
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      video.srcObject = stream;
      video.play();
      
      // Wait for video to be ready
      await new Promise((resolve) => {
        video.onloadedmetadata = resolve;
      });
      
      // Set canvas size to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      // Simple capture (you could build a better UI)
      const shouldCapture = window.confirm('Press OK to capture photo response');
      
      if (shouldCapture) {
        // Draw current video frame to canvas
        ctx.drawImage(video, 0, 0);
        
        // Convert to blob
        canvas.toBlob(async (blob) => {
          try {
            // Create File object for your service
            const capturedFile = new File([blob], `camera-capture-${Date.now()}.jpg`, {
              type: 'image/jpeg'
            });
            
            // Use your service to upload
            const uploadResult = await firestoreService.uploadFile(
              capturedFile, 
              `pb-${bookId}/reactions/photos/${mediaItem.id}`
            );
            
            await onReaction(mediaItem.id, 'photo', {
              photoUrl: uploadResult.url,
              fileName: uploadResult.fileName || uploadResult.originalFileName,
              originalName: 'Camera Capture',
              captureMethod: 'camera',
              timestamp: new Date()
            });
            
            alert('Camera photo saved successfully!');
            
          } catch (error) {
            console.error('Error saving camera capture:', error);
            alert('Failed to save camera photo');
          }
        }, 'image/jpeg', 0.8);
      }
      
      // Clean up camera stream
      stream.getTracks().forEach(track => track.stop());
      
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Camera not accessible. Using file picker instead.');
      setShowFileUpload(true);
    } finally {
      setUploadingReaction(false);
    }
  };

  const toggleVideoPlay = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      if (isVideoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsVideoPlaying(!isVideoPlaying);
    }
  };

  // Media Sharing Function
  const shareMedia = async (mediaItem) => {
    const shareData = {
      title: mediaItem.title || 'Check out this photo',
      text: mediaItem.description || 'Shared from album',
      url: `${window.location.origin}/media/${mediaItem.id}`
    };
    
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback to clipboard
        await navigator.clipboard.writeText(shareData.url);
        alert('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing:', error);
      // Manual fallback
      const textArea = document.createElement('textarea');
      textArea.value = shareData.url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Link copied to clipboard!');
    }
  };

  // Professional Download Function
  const downloadMedia = async (mediaItem) => {
    try {
      const { professionalConfig } = albumSettings;
      let downloadUrl = mediaItem.url;
      
      // Determine download quality based on permissions
      switch (professionalConfig?.downloadPermissions) {
        case 'compressed':
          downloadUrl = mediaItem.compressedUrl || mediaItem.url;
          break;
        case 'original':
          downloadUrl = mediaItem.originalUrl || mediaItem.url;
          break;
        default:
          return; // No download permission
      }
      
      // Track download for analytics
      if (albumType === 'professional') {
        await albumAnalyticsService.trackDownload(
          albumSettings.id, 
          mediaItem.id, 
          null, // userId if available
          professionalConfig.downloadPermissions
        );
      }
      
      // Trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = mediaItem.filename || `photo-${mediaItem.id}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error('Error downloading media:', error);
      alert('Download failed. Please try again.');
    }
  };

  // Audio Reaction Playback
  const playAudioReaction = (audioUrl) => {
    if (playingAudio === audioUrl) {
      // Stop current audio
      const audioElement = document.getElementById(`audio-${audioUrl.slice(-10)}`);
      audioElement?.pause();
      setPlayingAudio(null);
    } else {
      // Stop any currently playing audio
      if (playingAudio) {
        const currentAudio = document.getElementById(`audio-${playingAudio.slice(-10)}`);
        currentAudio?.pause();
      }
      
      // Play new audio
      const audioElement = new Audio(audioUrl);
      audioElement.id = `audio-${audioUrl.slice(-10)}`;
      audioElement.play();
      audioElement.onended = () => setPlayingAudio(null);
      setPlayingAudio(audioUrl);
    }
  };

  // Upload Functions (using Firebase Storage)
  const uploadAudioReaction = async (blob, docId) => {
    try {
      // Create File object from blob for your service
      const audioFile = new File([blob], `audio-reaction-${Date.now()}.webm`, {
        type: 'audio/webm'
      });
      
      // Use your albumService
      const uploadResult = await albumService.uploadFile(
        bookId, 
        audioFile, 
        `reactions/audio/${docId}`
      );
      
      return {
        url: uploadResult.url,
        fileName: uploadResult.fileName,
        originalName: uploadResult.originalName || audioFile.name,
        fileSize: uploadResult.fileSize,
        uploadedAt: uploadResult.uploadedAt
      };
      
    } catch (error) {
      console.error('Error uploading audio reaction:', error);
      throw new Error(`Failed to upload audio reaction: ${error.message}`);
    }
  };

  const uploadVideoReaction = async (blob, docId) => {
    try {
      // Create File object from blob
      const videoFile = new File([blob], `video-reaction-${Date.now()}.webm`, {
        type: 'video/webm'
      });
      
      // Use your albumService
      const uploadResult = await albumService.uploadFile(
        bookId,
        videoFile,
        `reactions/video/${docId}`
      );
      
      return {
        url: uploadResult.url,
        fileName: uploadResult.fileName,
        originalName: uploadResult.originalName || videoFile.name,
        fileSize: uploadResult.fileSize,
        uploadedAt: uploadResult.uploadedAt
      };
      
    } catch (error) {
      console.error('Error uploading video reaction:', error);
      throw new Error(`Failed to upload video reaction: ${error.message}`);
    }
  };

  const uploadPhotoResponse = async (file, docId) => {
    try {
      // Validate file first
      if (!file.type.startsWith('image/')) {
        throw new Error('Invalid file type. Please select an image.');
      }
      
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        throw new Error('Image too large. Please select a smaller image.');
      }
      
      // Use your firestoreService for photo responses
      const uploadResult = await firestoreService.uploadFile(
        file, 
        `pb-${bookId}/reactions/photos/${docId}`
      );
      
      return {
        url: uploadResult.url,
        fileName: uploadResult.fileName,
        originalName: uploadResult.originalFileName || file.name,
        fileSize: uploadResult.fileSize,
        uploadedAt: uploadResult.uploadedAt
      };
      
    } catch (error) {
      console.error('Error uploading photo response:', error);
      throw new Error(`Failed to upload photo response: ${error.message}`);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    if (mediaItem.type === 'video' && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const handleFieldChange = (fieldKey, value) => {
    setFieldValues(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  const handleSaveMetadata = async (e) => {
    e?.stopPropagation();
    
    if (!bookId || !mediaItem?.id || !galleryId) {
      console.error('Missing required data for saving');
      return;
    }

    setIsSaving(true);
    
    try {
      const metadataToUpdate = {};
      
      Object.entries(fieldValues).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          metadataToUpdate[key] = value;
        }
      });

      if (Object.keys(metadataToUpdate).length === 0) {
        console.log('No metadata changes to save');
        setIsEditing(false);
        return;
      }

      await albumService.updateMediaMetadata(
        bookId,
        galleryId,
        mediaItem.id,
        metadataToUpdate
      );

      if (onUpdate) {
        onUpdate();
      }

      setIsEditing(false);
      // ✅ KEEP CONTENT AREA VISIBLE AFTER SAVE (or set to false to hide)
      setShowContentArea(false); // Change to false if you want it to auto-hide

    } catch (error) {
      console.error('Error saving metadata:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (albumSettings?.enabledFields) {
      const normalizedMedia = normalizeMediaMetadata(mediaItem);
      const resetValues = {};
      
      Object.keys(albumSettings.enabledFields).forEach(fieldKey => {
        if (albumSettings.enabledFields[fieldKey]) {
          resetValues[fieldKey] = normalizedMedia[fieldKey] || '';
        }
      });
      setFieldValues(resetValues);
    }
    setIsEditing(false);
    setShowContentArea(false);
  };

  // RESTORED ORIGINAL INTERACTION HANDLERS
  const handleLike = async (e) => {
    console.log('🔵 LIKE clicked:', { 
      mediaId: mediaItem.id, 
      galleryId: mediaItem.galleryId,
      index 
    });
    e.stopPropagation();
    try {
      const newLikedState = await albumService.updateMediaInteraction(
        bookId, 
        mediaItem.galleryId, 
        mediaItem.id, 
        'like'
      );
      setIsLiked(newLikedState);
    } catch (error) {
      console.error('Error liking media:', error);
    }
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    const shareData = {
      title: fieldValues.title || normalizedMedia.title || 'Check out this media',
      text: fieldValues.description || normalizedMedia.description || '',
      url: `${window.location.origin}/book/${bookId}/media/${mediaItem.id}`
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData); 
      } catch (error) {
        console.log('Share cancelled or failed');
      }
    } else {
      navigator.clipboard.writeText(shareData.url);
      alert('Link copied to clipboard!');
    }
  };

  const handleDownload = async (e) => {
    e.stopPropagation();
    try {
      const response = await fetch(mediaItem.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = fieldValues.title || mediaItem.title || mediaItem.originalName || 'media-file';
      document.body.appendChild(a);
      a.click();
      
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleExpressionsClick = (e) => {
    console.log('💬 EXPRESSIONS clicked:', { 
      mediaId: mediaItem.id, 
      galleryId: mediaItem.galleryId,
      index 
    });
    e.stopPropagation();
    onOpenModal?.('expressions', mediaItem, galleryId);
  };
  
  const handleBooking = async (e, method) => {
    e.stopPropagation();
    
    const bookingConfig = gallery?.categoryConfig;
    
    setPendingTransaction({
      type: 'booking',
      method: method,
      config: bookingConfig
    });
    setShowTransactionConfirm(true);
  };

  const handleShopping = async (e, method) => {
    e.stopPropagation();
    
    const shoppingConfig = gallery?.categoryConfig;
    
    setPendingTransaction({
      type: 'shopping',
      method: method,
      config: shoppingConfig
    });
    setShowTransactionConfirm(true);
  };

  const confirmTransaction = async () => {
    if (!pendingTransaction) return;

    const { type, method, config } = pendingTransaction;
    const normalizedMedia = normalizeMediaMetadata(mediaItem);
    
    try {
      await albumService.recordMediaTransaction({
        albumId: bookId,
        galleryId: galleryId,
        mediaItemId: mediaItem.id,
        itemName: fieldValues.title || normalizedMedia.title || (type === 'booking' ? 'Service' : 'Item'),
        itemPrice: fieldValues.price || normalizedMedia.price || null,
        itemDescription: fieldValues.description || normalizedMedia.description || null,
        transactionType: type,
        method: method,
        userEmail: user?.email || null,
        userName: user?.displayName || null,
        userId: user?.uid || null,
        // NEW FIELDS
        startDate: transactionFormData.startDate || null,
        endDate: transactionFormData.endDate || null,
        quantity: transactionFormData.quantity || 1,
        area: transactionFormData.area || null
      });

      if (method === 'online') {
        const url = type === 'booking' ? config.bookingUrl : config.shoppingUrl;
        if (url) window.open(url, '_blank');
      } else if (method === 'openchat') {
        onOpenModal?.('openchat', mediaItem, galleryId);
      } else if (method === 'whatsapp') {
        handleTransactionWhatsApp(type, method, config);
      } else if (method === 'email') {
        handleTransactionEmail(type, method, config);
      } else if (method === 'link') {
        window.open((config.shoppingUrl || mediaItem.link), '_blank');
      }
    } catch (error) {
      console.error('Error processing transaction:', error);
    } finally {
      setShowTransactionConfirm(false);
      setPendingTransaction(null);
      setTransactionFormData({ startDate: '', endDate: '', quantity: 1, area: '' });
    }
  };

  const handleTransactionWhatsApp = (transactionType, method, config) => {
    const normalizedMedia = normalizeMediaMetadata(mediaItem);
    const itemName = fieldValues.title || normalizedMedia.title || 'Item';
    const itemPrice = fieldValues.price || normalizedMedia.price;
    const itemDescription = fieldValues.description || normalizedMedia.description || '';
    
    let message = '';
    
    if (transactionType === 'booking') {
      message = `🗓️ *BOOKING REQUEST*\n\n`;
      message += `*Service:* ${itemName}\n`;
      if (itemPrice) message += `*Price:* R${itemPrice}\n`;
      if (itemDescription) message += `*Description:* ${itemDescription}\n`;
      if (transactionFormData.area) message += `*Area:* ${transactionFormData.area}\n`;
      if (transactionFormData.quantity > 1) message += `*Quantity:* ${transactionFormData.quantity}\n`;
      message += `\n*Customer Details:*\n`;
      if (user?.displayName) message += `Name: ${user.displayName}\n`;
      if (user?.email) message += `Email: ${user.email}\n`;
      if (user?.phoneNumber) message += `Phone: ${user.phoneNumber}\n`;
      
      if (transactionFormData.startDate) {
        message += `\n📅 *Date:* ${new Date(transactionFormData.startDate).toLocaleString()}\n`;
        if (transactionFormData.endDate) {
          message += `📅 *End Date:* ${new Date(transactionFormData.endDate).toLocaleString()}\n`;
        }
      } else {
        message += `\n📅 *Preferred Date/Time:* [Please specify]\n`;
        if (config?.requireDateRange) {
          message += `📅 *End Date:* [Please specify]\n`;
        }
      }
      message += `\n💬 *Additional Notes:* [Any special requests]\n`;
    } else if (transactionType === 'shopping') {
      message = `🛒 *ORDER REQUEST*\n\n`;
      message += `*Item:* ${itemName}\n`;
      if (itemPrice) message += `*Price:* R${itemPrice}\n`;
      if (itemDescription) message += `*Description:* ${itemDescription}\n`;
      if (transactionFormData.area) message += `*Area:* ${transactionFormData.area}\n`;
      if (fieldValues.quantity || normalizedMedia.quantity) {
        message += `*Available Quantity:* ${fieldValues.quantity || normalizedMedia.quantity}\n`;
      }
      if (fieldValues.sku || normalizedMedia.sku) {
        message += `*SKU:* ${fieldValues.sku || normalizedMedia.sku}\n`;
      }
      message += `\n*Customer Details:*\n`;
      if (user?.displayName) message += `Name: ${user.displayName}\n`;
      if (user?.email) message += `Email: ${user.email}\n`;
      if (user?.phoneNumber) message += `Phone: ${user.phoneNumber}\n`;
      message += `\n📦 *Quantity Required:* ${transactionFormData.quantity}\n`;
      message += `📍 *Delivery Address:* [Please specify]\n`;
      message += `💬 *Additional Notes:* [Any special requests]\n`;
    }
    
    message += `\n🌐 *Source:* ${window.location.href}`;
    
    const phone = (config?.shoppingWhatsApp || config?.bookingWhatsApp || mediaItem.mobile || '').replace(/\D/g, '');
    const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleTransactionEmail = (transactionType, method, config) => {
    const normalizedMedia = normalizeMediaMetadata(mediaItem);
    const itemName = fieldValues.title || normalizedMedia.title || 'Item';
    const itemPrice = fieldValues.price || normalizedMedia.price;
    const itemDescription = fieldValues.description || normalizedMedia.description || '';
    
    let subject = '';
    let body = '';
    
    if (transactionType === 'booking') {
      subject = `Booking Request: ${itemName}`;
      body = `BOOKING REQUEST\n\n`;
      body += `Service: ${itemName}\n`;
      if (itemPrice) body += `Price: R${itemPrice}\n`;
      if (itemDescription) body += `Description: ${itemDescription}\n`;
      if (transactionFormData.area) body += `Area: ${transactionFormData.area}\n`;
      if (transactionFormData.quantity > 1) body += `Quantity: ${transactionFormData.quantity}\n`;
      body += `\nCustomer Details:\n`;
      if (user?.displayName) body += `Name: ${user.displayName}\n`;
      if (user?.email) body += `Email: ${user.email}\n`;
      if (user?.phoneNumber) body += `Phone: ${user.phoneNumber}\n`;
      
      if (transactionFormData.startDate) {
        body += `\nDate: ${new Date(transactionFormData.startDate).toLocaleString()}\n`;
        if (transactionFormData.endDate) {
          body += `End Date: ${new Date(transactionFormData.endDate).toLocaleString()}\n`;
        }
      } else {
        body += `\nPreferred Date/Time: [Please specify]\n`;
        if (config?.requireDateRange) {
          body += `End Date: [Please specify]\n`;
        }
      }
      body += `\nAdditional Notes: [Any special requests]\n`;
    } else if (transactionType === 'shopping') {
      subject = `Order Request: ${itemName}`;
      body = `ORDER REQUEST\n\n`;
      body += `Item: ${itemName}\n`;
      if (itemPrice) body += `Price: R${itemPrice}\n`;
      if (itemDescription) body += `Description: ${itemDescription}\n`;
      if (transactionFormData.area) body += `Area: ${transactionFormData.area}\n`;
      if (fieldValues.quantity || normalizedMedia.quantity) {
        body += `Available Quantity: ${fieldValues.quantity || normalizedMedia.quantity}\n`;
      }
      if (fieldValues.sku || normalizedMedia.sku) {
        body += `SKU: ${fieldValues.sku || normalizedMedia.sku}\n`;
      }
      body += `\nCustomer Details:\n`;
      if (user?.displayName) body += `Name: ${user.displayName}\n`;
      if (user?.email) body += `Email: ${user.email}\n`;
      if (user?.phoneNumber) body += `Phone: ${user.phoneNumber}\n`;
      body += `\nQuantity Required: ${transactionFormData.quantity}\n`;
      body += `Delivery Address: [Please specify]\n`;
      body += `Additional Notes: [Any special requests]\n`;
    }
    
    body += `\nSource: ${window.location.href}`;
    
    const email = config?.shoppingEmail || config?.bookingEmail || mediaItem.email;
    const mailtoUrl = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
  };

  const handlePhotoUpload = () => {
    onOpenModal?.('fileUpload', mediaItem, galleryId);
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    setIsMuted(!isMuted);
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
    }
  };

  const handleCardClick = (e) => {
    if (isEditing || e.target.closest('.overlay-actions')) {
      return;
    }
    onClick?.(mediaItem);
  };

  const enabledQuickStatuses = isTeamCategory 
    ? Object.entries(quickStatusConfig)
        .filter(([key, config]) => config.enabled && config.showOnCard)
        .map(([key, config]) => ({ key, ...config }))
    : [];

  // Handler for quick status button click
  const handleQuickStatusClick = async (statusType, option) => {
    try {
      setSelectedTeamStatus({ statusType, option });
      
      // Import the service
      const { teamStatusService } = await import('../../services/statusService');
      
      // Add status update
      await statusService.addStatusUpdate(bookId, galleryId, mediaItem.id, {
        itemType: 'member', // or 'customer', 'product', 'asset', etc.
        statusType: statusType,
        statusId: option.id,
        statusLabel: option.label,
        statusIcon: option.icon,
        statusColor: option.color,
        notes: '',
        recordedBy: user?.displayName || user?.email || 'Manager',
        recordedByEmail: user?.email,
        metadata: {
          quickAction: true,
          mediaItemTitle: mediaItem.title || mediaItem.metadata?.name || '',
          mediaItemName: mediaItem.metadata?.name || mediaItem.title || ''
        }
      });

      // Show success indicator
      setTimeout(() => setSelectedTeamStatus(null), 2000);
      
      // Refresh if needed
      if (onUpdate) onUpdate();
      
    } catch (error) {
      console.error('Error setting quick status:', error);
      setSelectedTeamStatus(null);
    }
  };

  const enabledFields = albumSettings?.enabledFields || {};
  const hasEnabledFields = Object.values(enabledFields).some(Boolean);

  // Get additional fields WITH VALUES (excluding title, description, and rating)
  const additionalFieldsWithValues = Object.entries(enabledFields).filter(([fieldKey, isEnabled]) => {
    if (!isEnabled || ['title', 'description', 'rating'].includes(fieldKey)) {
      return false;
    }
    
    // Check if this field actually has a value
    const value = fieldValues[fieldKey] || mediaItem.metadata?.[fieldKey] || mediaItem[fieldKey];
    return value && value.toString().trim() !== '';
  });
  

  // Only show toggle if there are actually additional fields with values
  const hasAdditionalContent = additionalFieldsWithValues.length > 0;

  const hasBasicContent = (fieldValues.title || mediaItem.title) || 
                       (fieldValues.description || mediaItem.description);

  const hasContentToShow = hasBasicContent || hasAdditionalContent;

  // Get rating value
  const ratingValue = fieldValues.rating || mediaItem.metadata?.rating || mediaItem.rating;

  const hasVoiceNotes = mediaItem.voiceNotes && mediaItem.voiceNotes.length > 0;

  const getContentTypeColor = (type) => {
    const colors = {
      'form': '#10b981',
      'article': '#3b82f6',
      'quiz': '#8b5cf6',
      'survey': '#06b6d4',
      'product': '#f59e0b',
      'event': '#ef4444',
      'profile': '#6366f1',
      'review': '#84cc16'
    };
    return colors[type] || '#6b7280';
  };

  const AITagsSection = () => (
    features.aiTagging && mediaItem.aiTags && (
      <div className="flex flex-wrap gap-1 mt-2">
        {mediaItem.aiTags.map((tag, index) => (
          <span 
            key={index}
            className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs"
          >
            <Sparkles className="h-3 w-3" />
            {tag.label} ({(tag.confidence * 100).toFixed(0)}%)
          </span>
        ))}
      </div>
    )
  );

  // Advanced Reactions Panel
  const AdvancedReactionsPanel = () => (
    <div className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-lg shadow-lg border p-3 z-20">
      <div className="grid grid-cols-4 gap-2">
        {/* Audio Reaction */}
        <button
          onClick={() => toggleAudioRecording()}
          className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-colors ${
            isRecordingAudio ? 'bg-red-100 text-red-600' : 'hover:bg-gray-100'
          }`}
        >
          <Mic className="h-4 w-4" />
          <span className="text-xs">Audio</span>
        </button>
        
        {/* Video Reaction */}
        <button
          onClick={() => toggleVideoRecording()}
          className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-colors ${
            isRecordingVideo ? 'bg-red-100 text-red-600' : 'hover:bg-gray-100'
          }`}
        >
          <Video className="h-4 w-4" />
          <span className="text-xs">Video</span>
        </button>
        
        {/* Photo Response */}
        <button
          onClick={() => capturePhotoResponse()}
          className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-gray-100"
        >
          <Camera className="h-4 w-4" />
          <span className="text-xs">Photo</span>
        </button>
        
        {/* AI Tags */}
        {features.aiTagging && (
          <button
            onClick={() => setShowAITags(!showAITags)}
            className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-gray-100"
          >
            <Tag className="h-4 w-4" />
            <span className="text-xs">Tags</span>
          </button>
        )}
      </div>
    </div>
  );

  // Professional Features Panel
  const ProfessionalPanel = () => (
    albumType === 'professional' && isOwner && (
      <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100">
        {mediaItem.analytics && (
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <Eye className="h-3 w-3" />
            {mediaItem.analytics.views || 0}
          </div>
        )}
        {mediaItem.downloadCount && (
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <Download className="h-3 w-3" />
            {mediaItem.downloadCount}
          </div>
        )}
      </div>
    )
  );

  const renderBadges = () => {
    if (!featured && !hero) return null;
    
    return (
      <div className="absolute top-2 left-2 z-10">
        {featured && (
          <span 
            className="px-2 py-1 rounded-full text-xs font-medium text-white"
            style={{ backgroundColor: primaryColor || '#3b82f6' }}
          >
            Featured
          </span>
        )}
        {hero && (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-600 text-white ml-1">
            Hero
          </span>
        )}
      </div>
    );
  };

  // NEW: Render layout-specific enhancements
  const renderLayoutEnhancements = () => {
    if (constellation) {
      return (
        <div className="absolute inset-0 border-2 border-transparent group-hover:border-white/50 rounded-full transition-colors" />
      );
    }
    
    if (riverFlow) {
      return (
        <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity -z-10" />
      );
    }
    
    return null;
  };

  const getTransactionButtonsWidth = () => {
    const booking = bookData?.transactions?.booking;
    const shopping = bookData?.transactions?.shopping;

    let activeButtons = 0;
    
    if (booking?.enabled && booking?.url) activeButtons++;
    if (shopping?.enabled && shopping?.url) activeButtons++;
    
    if (activeButtons === 0) return '0px';
    
    // Each button is ~40px width + gap, add some padding
    const buttonWidth = 40;
    const gap = 8;
    const padding = 8;
    
    return `${(activeButtons * buttonWidth) + ((activeButtons - 1) * gap) + padding}px`;
  };

  const renderFrontView = () => {
  if (!supportsFrontBack || isFlipped) return null;

  return (
    <>
      {/* ALL your existing content goes here - COMPLETE CARD */}
      {renderBadges()}
      
      {/* Media Container */}
      <div className="relative rounded-md overflow-hidden cursor-pointer isolate">
        <div className={getAspectClasses()}>
        {/* Rating Stars - Top Left */}
        {ratingValue && parseInt(ratingValue) > 0 && (
          <div className="absolute top-2 left-2 flex items-center gap-0.5 z-10">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className={`h-4 w-4 ${
                  star <= parseInt(ratingValue) 
                    ? 'text-yellow-400 fill-current' 
                    : 'text-gray-300 hover:text-yellow-200'
                }`}
              />
            ))}
            <span className="text-sm text-gray-600 ml-2">({ratingValue}/5)</span>
          </div>
        )}

        {/* Media Display */}
        <div className={getAspectClasses()}>
          {mediaItem.type === 'youtube' ? (
            // ===== YOUTUBE VIDEO =====
            <div className="relative w-auto h-full bg-black">
              <iframe
                src={`https://www.youtube.com/embed/${mediaItem.videoId}?rel=0&modestbranding=1`}
                title={mediaItem.title || 'YouTube video'}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className={getImageClasses()}
                loading="lazy"
              />  
            </div>
          ) : mediaItem.type === 'video' ? (
            // ===== UPLOADED VIDEO =====
            <div className="relative w-full h-full bg-black">
              <video
                ref={videoRef}
                className={getImageClasses()}
                muted={isMuted}
                loop
                playsInline
                preload="metadata"
                poster={mediaItem.thumbnailUrl}
                controls
                onPlay={() => setIsVideoPlaying(true)}
                onPause={() => setIsVideoPlaying(false)}
                onError={(e) => {
                  console.error('Video load error:', e);
                  e.target.poster = '/placeholder-video.jpg';
                }}
              >
                <source src={mediaItem.url} type={mediaItem.mimeType || 'video/mp4'} />
                Your browser does not support the video tag.
              </video>
          
              {/* Custom Video Controls Overlay (optional hover controls) */}
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 hover:bg-opacity-20 opacity-0 hover:opacity-100 transition-all pointer-events-none">
                <div className="flex items-center gap-4 pointer-events-auto">
                  <button
                    onClick={toggleVideoPlay}
                    className="p-4 bg-white bg-opacity-90 rounded-full hover:bg-opacity-100 transition-all shadow-lg"
                    title={isVideoPlaying ? 'Pause' : 'Play'}
                  >
                    {isVideoPlaying ? (
                      <Pause className="h-8 w-8 text-gray-900" />
                    ) : (
                      <Play className="h-8 w-8 text-gray-900 fill-current" />
                    )}
                  </button>
                </div>
                
                <button
                  onClick={toggleMute}
                  className="absolute bottom-4 right-4 p-2 bg-black bg-opacity-60 text-white rounded-full hover:bg-opacity-80 transition-all"
                  title={isMuted ? 'Unmute' : 'Mute'}
                >
                  {isMuted ? (
                    <VolumeX className="h-5 w-5" />
                  ) : (
                    <Volume2 className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="relative w-full h-full">
              <img
                src={mediaItem.url}
                alt={fieldValues.title || mediaItem.title || `Media ${index + 1}`}
                className={getImageClasses()}
                loading="eager"
                onError={(e) => {
                  e.target.style.display = 'none';
                  console.error('Image load error:', mediaItem.url);
                }}
              />
            </div>
          )}
        </div>

        {albumType === 'professional' && albumSettings.professionalConfig?.watermarking && (
          <div className="overlay-actions absolute bottom-2 right-2 text-white text-xs font-medium opacity-80 bg-black bg-opacity-50 px-2 py-1 rounded">
            {albumSettings.watermarkText || albumSettings.title}
          </div>
        )}

        {/* AI Tags Section */}
        {showAITags && <AITagsSection />}
        
        {/* Professional Analytics */}
        <ProfessionalPanel />

        {/* Swipe Hint for Mobile Devices Only - Updated to reflect new interactions */}
        {isMobileDevice && !showInteractionButtons && !isEditing && (
          <div className="absolute bottom-2 right-2 flex items-center gap-1 text-gray-400 text-xs opacity-60 pointer-events-none animate-pulse">
            <svg 
              className="w-4 h-4 animate-bounce" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span>Swipe for actions</span>
          </div>
        )}
        </div>

        {/* Interaction Buttons */}
        {isHovered && !isFlipped && (
         <div className="overlay-actions absolute top-3 right-3 flex flex-col gap-2 z-30 transition-all duration-300">
            {(() => {
              const interactions = [];
              const bookSettings = bookData;
              
              // Flip button - ADD THIS FIRST
              if (shouldShowFlipButton()) {
                interactions.push(
                  <button
                    key={`flip-${mediaItem.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsFlipped(true);
                    }}
                    className="bg-blue-600 text-white rounded-full p-2 transition-all duration-200"
                    title="View details"
                  >
                    <ChevronRight className="h-4 w-4" />
                    {backContentData && backContentData.type && (
                      <div 
                        className="absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-white"
                        style={{ backgroundColor: getContentTypeColor(backContentData.type) }}
                      />
                    )}
                  </button>
                );
              }

              {isMediaVoiceEnabled && canUserRecord() && (
                interactions.push(
                <button
                  key={`voice-${mediaItem.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    // Call parent callback instead of managing modal locally
                    onRecordVoice?.(mediaItem, galleryId);
                  }}
                  className="p-2 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition-colors shadow-lg"
                  title="Add voice note"
                >
                  <Mic className="w-5 h-5" />
                </button>
                )
              )}
              
              // ✅ FIXED: Priority 1: Likes
              if (bookSettings?.allowLikes !== false) {
                interactions.push(
                  <button
                    key={`likes-${mediaItem.id}`}
                    onClick={handleLike}
                    className={`rounded-full p-2 transition-all duration-200 ${
                      isLiked 
                        ? 'bg-red-500 text-white' 
                        : 'bg-gray-600 text-white'
                    }`}
                    title="Like"
                  >
                    <Heart className={`h-4 w-4 ${isLiked ? 'fill-current' : ''}`} />
                  </button>
                );
              }
              
              // ✅ FIXED: Priority 2: Comments  
              if (bookSettings?.allowComments !== false) {
                interactions.push(
                  <button
                    key={`comments-${mediaItem.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleExpressionsClick(e);
                    }}
                    className="relative bg-gray-600 text-white rounded-full p-2"
                    title="Comments"
                  >
                    <MessageCircle className="h-4 w-4" />
                    {(mediaItem.expressions?.totalCount || 0) > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-3 w-3 flex items-center justify-center text-[8px]">
                        {mediaItem.expressions.totalCount > 9 ? '9+' : mediaItem.expressions.totalCount}
                      </span>
                    )}
                  </button>
                );
              }
              
              // ✅ FIXED: Priority 5: Sharing
              if (bookSettings?.allowSharing !== false) {
                interactions.push(
                  <button
                    key={`sharing-${mediaItem.id}`}
                    onClick={handleShare}
                    className="bg-gray-600 text-white rounded-full p-2"
                    title="Share"
                  >
                    <Share2 className="h-4 w-4" />
                  </button>
                );
              }

              
              
              // ✅ FIXED: Priority 6: Downloads
              if (bookSettings?.allowDownloads !== false) {
                interactions.push(
                  <button
                    key={`downloads-${mediaItem.id}`}
                    onClick={handleDownload}
                    className="bg-gray-600 text-white rounded-full p-2"
                    title="Download"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                );
              }

              // Team Status Button for Team Category
              if (isTeamCategory && isOwner) {
                interactions.push(
                  <button
                    key={`team-status-${mediaItem.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowTeamStatusButtons(!showTeamStatusButtons);
                    }}
                    className={`${
                      showTeamStatusButtons 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-white/90 text-gray-600 hover:bg-white hover:text-blue-600'
                    } rounded-full p-2 transition-all duration-200 shadow-md`}
                    title="Quick Status"
                  >
                    <Activity className="h-4 w-4" />
                    {Object.keys(mediaItem).some(k => k.startsWith('currentStatus_')) && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                    )}
                  </button>
                );
              }
              
              // Limit visible interactions
              const maxVisible = isMobileDevice ? 3 : 4;
              const visibleInteractions = showAllInteractions 
                ? interactions 
                : interactions.slice(0, maxVisible);
              
              const hasMoreInteractions = interactions.length > maxVisible;
    
              return (
                <>
                  {/* Render visible interactions */}
                  {visibleInteractions}
                  
                  {/* More/Less toggle if needed */}
                  {hasMoreInteractions && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowAllInteractions(!showAllInteractions);
                        
                        if (isMobileDevice && !showAllInteractions) {
                          if (touchTimeoutRef.current) {
                            clearTimeout(touchTimeoutRef.current);
                          }
                          touchTimeoutRef.current = setTimeout(() => {
                            setShowInteractionButtons(false);
                            setShowAllInteractions(false);
                          }, 5000);
                        }
                      }}
                      className="bg-gray-700 text-white rounded-full p-2 transition-all duration-200"
                      title={showAllInteractions ? 'Show Less' : 'More Options'}
                    >
                      {showAllInteractions ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <MoreHorizontal className="h-4 w-4" />
                      )}
                    </button>
                  )}
                  
                  {/* Edit Button - Only for Owner */}
                  {isOwner && (
                    <button
                      key={`edit-${mediaItem.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        
                        // Initialize fieldValues with existing content
                        if (albumSettings?.enabledFields) {
                          const initialValues = {};
                          Object.keys(albumSettings.enabledFields).forEach(fieldKey => {
                            if (albumSettings.enabledFields[fieldKey]) {
                              const value = mediaItem.metadata?.[fieldKey] || mediaItem[fieldKey];
                              // Only set if value exists
                              if (value !== undefined && value !== null && value !== '') {
                                initialValues[fieldKey] = value;
                              }
                            }
                          });
                          setFieldValues(initialValues);
                        }
                        
                        setIsEditing(true);
                        setShowContentArea(true);
                        
                        if (isMobileDevice && touchTimeoutRef.current) {
                          clearTimeout(touchTimeoutRef.current);
                        }
                      }}
                      className="bg-gray-500 text-white hover:bg-white/80 hover:text-gray-600 rounded-full p-2 transition-all duration-200"
                      title="Edit"
                    >
                      <Edit3 className="h-4 w-4" />
                    </button>
                  )}
                </>
              );
            })()}

            {/* Team Status Popup */}
            {isTeamCategory && isOwner && showTeamStatusButtons && (
              <div 
                className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 p-3 z-50 min-w-[300px]"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="space-y-3">
                  {enabledQuickStatuses.map(({ key, label, icon: StatusIcon, options }) => {
                    const IconComponent = StatusIcon || Activity;
                    
                    return (
                      <div key={key} className="space-y-2">
                        <div className="flex items-center gap-2 text-xs font-medium text-gray-600 uppercase tracking-wide">
                          {typeof IconComponent === 'function' ? (
                            <IconComponent className="h-3 w-3" />
                          ) : (
                            <Activity className="h-3 w-3" />
                          )}
                          <span>{label}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-1.5">
                          {options.slice(0, 4).map((option) => {
                            const isCurrentStatus = mediaItem[`currentStatus_${key}`]?.statusId === option.id;
                            const isSelected = selectedTeamStatus?.statusType === key && selectedTeamStatus?.option.id === option.id;
                            
                            return (
                              <button
                                key={option.id}
                                onClick={() => handleQuickStatusClick(key, option)}
                                disabled={isSelected}
                                className={`
                                  flex items-center gap-1.5 px-2 py-1.5 rounded text-xs font-medium
                                  transition-all duration-200 border-2
                                  ${isCurrentStatus 
                                    ? 'border-current shadow-sm' 
                                    : 'border-transparent hover:border-gray-200'
                                  }
                                  ${isSelected ? 'animate-pulse' : ''}
                                `}
                                style={{
                                  backgroundColor: isCurrentStatus ? `${option.color}15` : '#f9fafb',
                                  color: isCurrentStatus ? option.color : '#374151'
                                }}
                                title={option.description}
                              >
                                <span className="text-base leading-none">
                                  {typeof option.icon === 'string' ? option.icon : '●'}
                                </span>
                                <span className="truncate">{option.label.split(' ')[0]}</span>
                                {isCurrentStatus && <Check className="h-3 w-3 ml-auto flex-shrink-0" />}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowTeamStatusButtons(false);
                      if (onOpenModal) {
                        onOpenModal(mediaItem, 'teamStatus');
                      }
                    }}
                    className="w-full px-3 py-2 text-xs text-blue-600 hover:bg-blue-50 rounded-lg transition-colors font-medium flex items-center justify-center gap-1"
                  >
                    <MoreHorizontal className="h-3 w-3" />
                    More Options & Notes
                  </button>
                </div>
              </div>
            )}

            {/* Advanced Reactions Panel */}
            {showAdvancedReactions && <AdvancedReactionsPanel />}
          </div>
        )}
    
        {(gallery?.categoryConfig?.enableBooking || gallery?.categoryConfig?.enableShopping) &&(
          <div 
            className="overlay-actions absolute bottom-2 left-2 flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Booking Buttons - Multiple Methods */}
            {gallery?.categoryConfig?.enableBooking && (() => {
              const methods = gallery.categoryConfig.bookingMethods || {};
              const buttons = [];

              if (methods.online && (gallery.categoryConfig.bookingUrl || mediaItem.link)) {
                buttons.push(
                  <button
                    key="book-online"
                    onClick={(e) => handleBooking(e, 'online')}
                    className="bg-gradient-to-br from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 rounded-full p-2 transition-all duration-200 shadow-lg hover:shadow-xl"
                    title="Book Online"
                  >
                    <Calendar className="h-4 w-4" />
                  </button>
                );
              }
              
              if (methods.openchat) {
                buttons.push(
                  <button
                    key="book-openchat"
                    onClick={(e) => handleBooking(e, 'openchat')}
                    className="bg-[#0795ff]/60 text-gray-200 hover:bg-[#0795ff] hover:text-white rounded-full shadow-md p-2 transition-all duration-200 shadow-lg hover:shadow-xl"
                    title="Book via Chat"
                  >
                    <MessageSquare className="h-4 w-4" />
                  </button>
                );
              }
              
              if (methods.whatsapp && (gallery.categoryConfig.bookingWhatsApp || mediaItem.mobile)) {
                buttons.push(
                  <button
                    key="book-whatsapp"
                    onClick={(e) => handleBooking(e, 'whatsapp')}
                    className="bg-green-500/30 text-gray-200 hover:bg-green-600 hover:text-white rounded-full shadow-md p-2 transition-all duration-200 shadow-lg hover:shadow-xl"
                    title="Book via WhatsApp"
                  >
                    <MessageCircle className="h-4 w-4" />
                  </button>
                );
              }

              return buttons.length > 0 ? buttons : null;
            })()}

            {/* Shopping Buttons - Multiple Methods */}
            {gallery?.categoryConfig?.enableShopping &&(() => {
              const methods = gallery.categoryConfig.shoppingMethods || {};
              const buttons = [];

              if (methods.link && (gallery.categoryConfig.shoppingUrl || mediaItem.link)) {
                buttons.push(
                  <button
                    key="shop-link"
                    onClick={(e) => handleShopping(e, 'link')}
                    className="bg-gradient-to-br from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 rounded-full p-2 transition-all duration-200 shadow-lg hover:shadow-xl"
                    title="Shop Now"
                  >
                    <ShoppingCart className="h-4 w-4" />
                  </button>
                );
              }

              if (methods.whatsapp && (gallery.categoryConfig.shoppingWhatsApp || mediaItem.mobile)) {
                buttons.push(
                  <button
                    key="shop-whatsapp"
                    onClick={(e) => handleShopping(e, 'whatsapp')}
                    className="bg-gradient-to-br from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 rounded-full p-2 transition-all duration-200 shadow-lg hover:shadow-xl"
                    title="Order via WhatsApp"
                  >
                    <MessageCircle className="h-4 w-4" />
                  </button>
                );
              }

              if (methods.email && (gallery.categoryConfig.shoppingEmail || mediaItem.email)) {
                buttons.push(
                  <button
                    key="shop-email"
                    onClick={(e) => handleShopping(e, 'email')}
                    className="bg-gradient-to-br from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 rounded-full p-2 transition-all duration-200 shadow-lg hover:shadow-xl"
                    title="Order via Email"
                  >
                    <Mail className="h-4 w-4" />
                  </button>
                );
              }

              return buttons.length > 0 ? buttons : null;
            })()}
          </div>
        )}

        {/* Quick Stats - Bottom Left */}
        {bookData?.showInteractionStats && (
          <div 
            className={`absolute bottom-2 right-2 flex items-center gap-1 bg-black/60 text-white text-xs px-2 py-1 rounded transition-all duration-200 ${
              // Check if any transaction buttons are active and adjust positioning
              (bookData?.transactions?.booking?.enabled && bookData?.transactions?.booking?.url) ||
              (bookData?.transactions?.shopping?.enabled && bookData?.transactions?.shopping?.url)
                ? 'transform translate-x-0' // Normal position when transactions are present
                : ''
            }`}
            style={{
              // Dynamic right margin based on transaction button width
              marginRight: getTransactionButtonsWidth()
            }}
          >
            {/* Views */}
            {bookData?.showViewCounts && mediaItem.interactions?.views > 0 && (
              <span className="flex items-center gap-1">
                <Eye className="h-3 w-3" />
                {mediaItem.interactions.views}
              </span>
            )}
            
            {/* Likes */}
            {mediaItem.interactions?.likes > 0 && (
              <span className="flex items-center gap-1">
                <Heart className="h-3 w-3" />
                {mediaItem.interactions.likes}
              </span>
            )}
            
            {/* Comments */}
            {mediaItem.interactions?.comments > 0 && (
              <span className="flex items-center gap-1">
                <MessageCircle className="h-3 w-3" />
                {mediaItem.interactions.comments}
              </span>
            )}
          </div>
        )}

        {isMediaVoiceEnabled && hasVoiceNotes && (
          <div onClick={(e) => e.stopPropagation()}>
            <MediaVoiceIndicator
              voiceNotes={mediaItem.voiceNotes}
              indicatorStyle={voiceSettings?.mediaVoiceNotes?.indicatorStyle || 'waveform'}
              onClick={(e) => {
                e.stopPropagation();
                setShowVoicePanel(true);
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};

  // BACK VIEW HEADER - Following CardRenderer pattern
  const renderBackViewHeader = () => {
    if (!supportsFrontBack || !isFlipped) return null;

    return (
      <div className="relative">
        {/* Smaller banner in back view */}
        {mediaItem.url && (
          <div className="h-24 rounded-t-lg overflow-hidden">
            <img 
              src={mediaItem.url}
              alt={mediaItem.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Header with back button */}
        <div className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3 flex-1">
              {/* Back Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsFlipped(false);
                }}
                className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors mt-1"
                title="Back to media"
              >
                <ChevronLeft className="w-5 h-5 text-gray-700" />
              </button>
              
              {/* Title and info */}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 text-lg leading-tight">
                  {mediaItem.title || 'Media Item'}
                </h3>
                {mediaItem.description && (
                  <p className="text-gray-600 text-sm mt-1 line-clamp-2">
                    {mediaItem.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // BACK VIEW CONTENT - This is where FlipCardContent goes
  const renderBackViewContent = () => {
    if (!supportsFrontBack || !isFlipped) return null;

    return (
      <div className="px-4 pb-4">
        <FlipCardContent
          content={backContentData}
          mediaItem={mediaItem}
          bookId={bookId}
          galleryId={galleryId}
          isOwner={isOwner}
          primaryColor={primaryColor}
          onFlipBack={() => setIsFlipped(false)}
          onUpdate={onUpdate}
          onAddContent={onContentCreate}
          isLoading={loadingBackContent}
          bookData={bookData}
          // Remove the back button from FlipCardContent since we have it in header
          showBackButton={true}
        />
      </div>
    );
  };

  return (
  <div 
    ref={cardRef}
    className="flip-card-wrapper relative group"
    style={{ perspective: '1000px' }}
    onClick={handleCardClick}
    onTouchStart={handleTouchStart}
    onTouchMove={handleTouchMove}
    onTouchEnd={handleTouchEnd}
    onMouseEnter={handleMouseEnter}
    onMouseLeave={handleMouseLeave}
  >

    {/* For non-flippable cards, show normal image */}
    {!supportsFrontBack && !isFlipped && (
      <div className={getCardClasses()}>
        {renderBadges()}
        
        <div className="relative shadow-lg overflow-hidden">
          <div className={getAspectClasses()}>
            {mediaItem.type === 'video' ? (
              <video
                className="w-full h-full object-cover"
                poster={mediaItem.thumbnailUrl || mediaItem.url}
                controls={false}
                muted
                playsInline
              >
                <source src={mediaItem.url} type="video/mp4" />
              </video>
            ) : (
              <img
                src={mediaItem.url}
                alt={mediaItem.title || 'Media item'}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            )}
          </div>
        </div>
      </div>
    )}

    {/* Show front view - ALWAYS when not flipped, regardless of editing state */}
    {!isFlipped && (() => {
    const position = gallery?.categoryConfig?.contentAreaPosition || 'bottom';
    const allowToggle = gallery?.categoryConfig?.toggleContentArea !== false;
    const shouldShowContentArea = gallery?.categoryConfig?.showContentArea !== false;
    const hasContent = hasBasicContent || hasAdditionalContent;
    
    // Get custom styling colors
    const bgColor = customStyling?.backgroundColor || bookData?.customStyling?.backgroundColor || '#ffffff';
    const textColor = customStyling?.textColor || bookData?.customStyling?.textColor || '#000000';
    
    const isContentVisible = shouldShowContentArea && (isEditing || (hasContent && (!allowToggle || showContentArea)))
    
    const contentAreaElement = isContentVisible && (
      <div
        className={`space-y-3 p-2 ${
          position === 'top' ? 'border-b rounded-t-lg' : 'border-t rounded-b-lg'
        } animate-in ${position === 'top' ? 'slide-in-from-top-2' : 'slide-in-from-bottom-2'} duration-200`}
        style={{ 
          backgroundColor: bgColor,
          color: textColor,
          borderColor: `${textColor}20`
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {isEditing ? (
          <div className="space-y-4">
            {(() => {
              const fieldOrder = [
                'title', 'description', 'category', 'price', 'link',
                'email', 'mobile', 'location', 'date', 'rating',
                'status', 'tags', 'notes'
              ];

              const sortedFields = Object.entries(enabledFields)
                .filter(([_, isEnabled]) => isEnabled)
                .sort(([keyA], [keyB]) => {
                  const indexA = fieldOrder.indexOf(keyA);
                  const indexB = fieldOrder.indexOf(keyB);
                  if (indexA === -1 && indexB === -1) return 0;
                  if (indexA === -1) return 1;
                  if (indexB === -1) return -1;
                  return indexA - indexB;
                });

              return sortedFields.map(([fieldKey, _]) => {
                const fieldConfig = {
                  label: fieldKey.charAt(0).toUpperCase() + fieldKey.slice(1),
                  type: getFieldType(fieldKey),
                  ...albumSettings.MEDIA_CARD_FIELDS?.[fieldKey]
                };
                
                return (
                  <DynamicFieldInput
                    fieldKey={fieldKey}
                    fieldConfig={fieldConfig}
                    value={fieldValues[fieldKey] || ''}
                    onChange={handleFieldChange}
                    isEditing={isEditing}
                    mediaItem={mediaItem}
                    albumSettings={albumSettings}
                    customStyling={customStyling}
                    bookId={bookId}
                    galleryId={galleryId}
                    mediaItemId={mediaItem.id}
                  />
                );
              });
            })()}

            <div className="flex gap-2 py-2">
              <button
                onClick={handleSaveMetadata}
                disabled={isSaving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {isSaving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={handleCancelEdit}
                disabled={isSaving}
                style={{ 
                  backgroundColor: `${textColor}10`,
                  color: textColor,
                  borderColor: `${textColor}30`
                }}
                className="px-4 py-2 border hover:opacity-80 rounded-lg text-sm font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
          {/* Title with optional brand logo and link icon */}
          {(fieldValues.title || mediaItem.title) && (
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {/* Brand Logo or Favicon */}
                {(enabledFields.brandLogo || bookData?.favicon) && (
                  <div className="flex-shrink-0">
                    <img 
                      src={
                        (enabledFields.brandLogo && (fieldValues.brandLogo || mediaItem.metadata?.brandLogo)) 
                        || bookData?.favicon 
                        || bookData?.settings?.favicon
                      }
                      alt="Logo" 
                      className="h-10 w-10 object-contain rounded-full"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                
                {/* Title */}
                <h3 className="text-md font-semibold line-clamp-2 flex-1 min-w-0">
                  {fieldValues.title || mediaItem.title}
                </h3>
              </div>
              
              {/* External Link Icon */}
              {(fieldValues.link || mediaItem.metadata?.link || mediaItem.link) && (
                <a
                  href={fieldValues.link || mediaItem.metadata?.link || mediaItem.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="flex-shrink-0 p-1 text-blue-600 hover:text-blue-800 transition-colors"
                  title="Open link"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
            </div>
          )}
            {(fieldValues.description || mediaItem.description) && (
              <p className="text-sm line-clamp-3" style={{ color: `${textColor}dd` }}>
                {fieldValues.description || mediaItem.description}
              </p>
            )}
            {hasAdditionalContent && (
              <>
                {showAllFields && (
                  <div className="space-y-3 pt-2" style={{ borderTopColor: `${textColor}20`, borderTopWidth: '1px' }}>
                    {additionalFieldsWithValues.map(([fieldKey, _]) => {
                      const value = fieldValues[fieldKey] || mediaItem.metadata?.[fieldKey] || mediaItem[fieldKey];
                      const fieldConfig = {
                        label: fieldKey.charAt(0).toUpperCase() + fieldKey.slice(1),
                        type: getFieldType(fieldKey),
                        ...albumSettings.MEDIA_CARD_FIELDS?.[fieldKey]
                      };
                      return (
                        <div key={fieldKey} className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium" style={{ color: `${textColor}99` }}>
                              {fieldConfig.label}:
                            </span>
                          </div>
                          <DynamicFieldInput
                            fieldKey={fieldKey}
                            fieldConfig={fieldConfig}
                            value={value}
                            onChange={handleFieldChange}
                            isEditing={false}
                            albumSettings={albumSettings}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAllFields(!showAllFields);
                  }}
                  className="flex items-center gap-2 text-xs hover:opacity-80 transition-colors"
                  style={{ color: `${textColor}99` }}
                >
                  {showAllFields ? (
                    <><ChevronUp className="h-4 w-4" />Show Less</>
                  ) : (
                    <><ChevronDown className="h-4 w-4" />Show More ({additionalFieldsWithValues.length})</>
                  )}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    );

    const toggleButton = hasContent && shouldShowContentArea && allowToggle && !isEditing && (
      <button
        onClick={(e) => {
          e.stopPropagation();
          setShowContentArea(!showContentArea);
        }}
        className={`absolute ${position === 'top' ? 'top-1' : 'bottom-1'} right-1 p-1 rounded-full hover:shadow-lg transition-all duration-200 z-10`}
        style={{ 
          backgroundColor: `${textColor}10`,
          color: textColor
        }}
        title={showContentArea ? "Hide details" : "Show details"}
      >
        {showContentArea ? (
          position === 'top' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
        ) : (
          <Eye className="h-4 w-4" />
        )}
      </button>
    );

    return (
      <>
        {position === 'top' && contentAreaElement}
        {position === 'top' && toggleButton}
        {supportsFrontBack ? renderFrontView() : null}
        {position === 'bottom' && toggleButton}
        {position === 'bottom' && contentAreaElement}
      </>
    );
  })()}

    {/* Back View - only when flipped */}
    {isFlipped && renderBackViewContent()}

    {/* Transaction Confirmation Modal */}
    {showTransactionConfirm && pendingTransaction && (
      <div 
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={(e) => {
          e.stopPropagation();
          setShowTransactionConfirm(false);
          setPendingTransaction(null);
          setTransactionFormData({ startDate: '', endDate: '', quantity: 1, area: '' });
        }}
      >
        <div 
          className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start gap-4 mb-6">
            <div className={`p-3 rounded-full ${
              pendingTransaction.method === 'whatsapp' ? 'bg-green-100' :
              pendingTransaction.method === 'email' ? 'bg-blue-100' :
              pendingTransaction.method === 'openchat' ? 'bg-purple-100' :
              pendingTransaction.type === 'booking' ? 'bg-blue-100' : 
              'bg-green-100'
            }`}>
              {pendingTransaction.method === 'whatsapp' ? (
                <MessageCircle className="h-6 w-6 text-green-600" />
              ) : pendingTransaction.method === 'email' ? (
                <Mail className="h-6 w-6 text-blue-600" />
              ) : pendingTransaction.method === 'openchat' ? (
                <MessageSquare className="h-6 w-6 text-purple-600" />
              ) : pendingTransaction.type === 'booking' ? (
                <Calendar className="h-6 w-6 text-blue-600" />
              ) : (
                <ShoppingCart className="h-6 w-6 text-green-600" />
              )}
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                 {pendingTransaction.type ? 'Booking' : 'Order'} Query
              </h3>
              <p className="text-xs text-gray-600">
                {fieldValues.title || normalizeMediaMetadata(mediaItem).title || 'Item'}
              </p>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowTransactionConfirm(false);
                setPendingTransaction(null);
                setTransactionFormData({ startDate: '', endDate: '', quantity: 1, area: '' });
              }}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-400" />
            </button>
          </div>

          {/* Form Fields */}
          <div className="space-y-4 mb-6">
            {/* Date Selection - Booking Only */}
            {pendingTransaction.type === 'booking' && (
              <>
                {pendingTransaction.config?.requireDateRange ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Start Date
                      </label>
                      <input
                        type="datetime-local"
                        value={transactionFormData.startDate}
                        onChange={(e) => setTransactionFormData(prev => ({ ...prev, startDate: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        End Date
                      </label>
                      <input
                        type="datetime-local"
                        value={transactionFormData.endDate}
                        onChange={(e) => setTransactionFormData(prev => ({ ...prev, endDate: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Date & Time
                    </label>
                    <input
                      type="datetime-local"
                      value={transactionFormData.startDate}
                      onChange={(e) => setTransactionFormData(prev => ({ ...prev, startDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                )}
              </>
            )}

            {/* Quantity Field */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Quantity
              </label>
              <input
                type="number"
                min="1"
                value={transactionFormData.quantity}
                onChange={(e) => setTransactionFormData(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Area Dropdown */}
            {(() => {
              const normalizedMedia = normalizeMediaMetadata(mediaItem);
              const areas = normalizedMedia.areas;
              
              if (areas && Array.isArray(areas) && areas.length > 0) {
                return (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Area
                    </label>
                    <select
                      value={transactionFormData.area}
                      onChange={(e) => setTransactionFormData(prev => ({ ...prev, area: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select Area</option>
                      {areas.map((area, index) => (
                        <option key={index} value={area}>{area}</option>
                      ))}
                    </select>
                  </div>
                );
              }
              return null;
            })()}
          </div>

          <div className="flex gap-3">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowTransactionConfirm(false);
                setPendingTransaction(null);
                setTransactionFormData({ startDate: '', endDate: '', quantity: 1, area: '' });
              }}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg text-xs hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                confirmTransaction();
              }}
              className={`flex-1 px-4 py-2 text-white rounded-lg text-xs transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 ${
                pendingTransaction.method === 'whatsapp' 
                  ? 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800'
                  : pendingTransaction.method === 'email'
                  ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
                  : pendingTransaction.method === 'openchat'
                  ? 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800'
                  : pendingTransaction.type === 'booking'
                  ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
                  : 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800'
              }`}
            >
              {pendingTransaction.method === 'whatsapp' ? (
                <>
                  <MessageCircle className="h-4 w-4" />
                  Open WhatsApp
                </>
              ) : pendingTransaction.method === 'email' ? (
                <>
                  <Mail className="h-4 w-4" />
                  Open Email
                </>
              ) : pendingTransaction.method === 'openchat' ? (
                <>
                  <MessageSquare className="h-4 w-4" />
                  Open Chat
                </>
              ) : pendingTransaction.type === 'booking' ? (
                <>
                  <Calendar className="h-4 w-4" />
                  Confirm Booking
                </>
              ) : (
                <>
                  <ShoppingCart className="h-4 w-4" />
                  Confirm Order
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* VOICE NOTES INLINE PANEL - Expands within card */}
    {showVoicePanel && isMediaVoiceEnabled && (
      <MediaVoiceNotes
        user={user}
        galleryId={galleryId}
        bookId={bookId}
        mediaItem={mediaItem}
        voiceSettings={voiceSettings}
        isOwner={isOwner}
        canUserRecord={canUserRecord()}
        onRecord={() => {
          setShowVoicePanel(false);
          onRecordVoice?.(mediaItem, galleryId);
        }}
        onClose={() => setShowVoicePanel(false)}
      />
    )}
  </div>
);

};

// Helper function to determine field type
const getFieldType = (fieldKey) => {
  const typeMap = {
    title: 'text',
    description: 'textarea',
    price: 'currency',
    rating: 'rating',
    link: 'url',
    tags: 'tags',
    category: 'select',
    availability: 'status',
    projectDate: 'date',
    uploadDate: 'date',
    quantity: 'number',
    calories: 'number',
    spiceLevel: 'rating'
  };
  
  return typeMap[fieldKey] || 'text';
};
